import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { CONTACT } from "../data/content";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", subject: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — I'll be in touch soon.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-white/15 py-4 text-lg text-white placeholder:text-zinc-600 focus:border-gold focus:outline-none transition-colors duration-300";

  return (
    <section
      id="contact"
      className="relative mx-auto max-w-[1400px] px-6 py-28 lg:px-10 lg:py-40"
      data-testid="contact-section"
    >
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>{CONTACT.overline}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              {CONTACT.title}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-400">
              {CONTACT.body}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-6 max-w-md font-serif text-xl italic text-zinc-300">
              {CONTACT.tag}
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 flex flex-col gap-3">
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="inline-block w-fit text-lg text-gold underline-offset-8 hover:underline"
              >
                {CONTACT.email}
              </a>
              <a
                href={`tel:${CONTACT.phoneHref}`}
                data-testid="contact-phone-link"
                className="inline-block w-fit text-lg text-zinc-300 underline-offset-8 transition-colors duration-300 hover:text-gold hover:underline"
              >
                {CONTACT.phone}
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="flex flex-col gap-8" data-testid="contact-form">
              <div className="grid gap-8 sm:grid-cols-2">
                <input
                  className={inputClass}
                  placeholder="Your name"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-name-input"
                />
                <input
                  className={inputClass}
                  placeholder="Email address"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-email-input"
                />
              </div>
              <input
                className={inputClass}
                placeholder="Subject (optional)"
                value={form.subject}
                onChange={update("subject")}
                data-testid="contact-subject-input"
              />
              <textarea
                className={`${inputClass} min-h-[120px] resize-none`}
                placeholder="Tell me about your project"
                value={form.message}
                onChange={update("message")}
                data-testid="contact-message-input"
              />
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                data-testid="contact-submit-button"
                className="group inline-flex w-full items-center justify-center gap-3 border border-white/20 bg-white px-8 py-4 text-black transition-colors duration-300 hover:bg-gold disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> Sending…
                  </>
                ) : (
                  <>
                    Send message
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </>
                )}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
